import { useState, useEffect, useRef, PropsWithChildren } from 'react'

import useViewport from 'libs/use-viewport'

import { debounce } from 'utils/debounce'

import cn from 'classnames'

import IconInfoBlue from 'images/icon-info-blue.svg'
import NoticeIcon from 'images/notice-icon.svg'

import { ReactSVG } from 'react-svg'
import { useTranslation } from 'utils/use-translation'

import styles from './styles.module.scss'

function getCoords(elem: HTMLElement) {
  const box = elem.getBoundingClientRect()

  const body = document.body
  const docEl = document.documentElement

  const scrollTop = window.pageYOffset || docEl.scrollTop || body.scrollTop
  const scrollLeft = window.pageXOffset || docEl.scrollLeft || body.scrollLeft

  const clientTop = docEl.clientTop || body.clientTop || 0
  const clientLeft = docEl.clientLeft || body.clientLeft || 0

  const top = box.top + scrollTop - clientTop
  const left = box.left + scrollLeft - clientLeft

  return { top: Math.round(top), left: Math.round(left) }
}

interface NoticeProps {
  blue?: boolean
  big?: boolean
  alt: string
  questionPosition?: string
  boxPosition?: string
  leftPos?: string
  boxStyle?: string
  imgClassName?: string
  wrapperClassName?: string
  type?: 'primary' | 'secondary'
  arrowOffsetX?: number
  className?: string
}

export default function Notice({
  children,
  alt,
  questionPosition,
  boxPosition,
  blue,
  big,
  leftPos,
  imgClassName,
  wrapperClassName,
  type = 'primary',
  arrowOffsetX = 0,
  className,
}: PropsWithChildren<NoticeProps>) {
  const { t } = useTranslation()
  const elementRefs = useRef<{
    box?: HTMLDivElement
    img?: HTMLDivElement
  }>({})
  const [viewport] = useViewport()
  const [left, setLeft] = useState(0)
  const [top, setTop] = useState(0)
  const [arrowLeft, setArrowLeft] = useState(0)

  const isMobile = viewport.includes('xs-mobile')

  const updateSizes = () => {
    if (!elementRefs.current.box || !elementRefs.current.img) {
      return
    }

    const boxWidth = elementRefs.current.box.clientWidth
    const boxHeight = elementRefs.current.box.clientHeight
    const imgWidth = elementRefs.current.img.clientWidth
    const imgHeight = elementRefs.current.img.clientHeight
    const imgCoors = getCoords(elementRefs.current.img)

    const newLeft = imgCoors.left - Math.min(imgCoors.left + imgWidth + 10, window.innerWidth - 10 - boxWidth)
    const newTop = imgHeight + 10

    if (newLeft !== left) setLeft(newLeft)
    if (newTop !== top) {
      setTop(type === 'secondary' ? boxHeight : newTop)
    }
    if (isMobile) {
      setArrowLeft(imgCoors.left - 14 + arrowOffsetX)
    }
  }

  const handleScroll = debounce(updateSizes, 16.6)

  useEffect(() => {
    updateSizes()
    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  useEffect(() => {
    updateSizes()
  }, [viewport, elementRefs.current?.img, elementRefs.current?.box, t, isMobile])

  return (
    <div
      className={cn(
        styles.noticeWrapper,
        wrapperClassName,
        {
          [styles.secondary]: type === 'secondary',
        },
        className,
      )}
    >
      <div className={cn(styles.notice, questionPosition)}>
        <div
          className={cn(styles.notice_img, { [styles.notice_img_big]: big }, imgClassName)}
          ref={(el) => {
            elementRefs.current.img = el
          }}
        >
          {type === 'secondary' ? (
            <div className={cn(styles.secondary__icon)}></div>
          ) : (
            <ReactSVG src={blue ? IconInfoBlue : NoticeIcon} />
          )}
        </div>

        <div
          ref={(el) => {
            elementRefs.current.box = el
          }}
          className={cn(styles.box, boxPosition)}
          style={
            type === 'secondary'
              ? {
                  left: isMobile ? '0' : `-21px`,
                  top: isMobile ? `-${top + 4}px` : `-${top + 8}px`,
                  width: '320px',
                  height: 'fit-content',
                  transformOrigin: isMobile ? `${arrowLeft}px 100%` : '24px 100%',
                }
              : isMobile
              ? {
                  left: `${left}px`,
                  top: `${top}px`,
                }
              : null
          }
        >
          <p className={styles.noticeText}>{children}</p>
          {type === 'secondary' && (
            <div className={styles.arrow} style={{ left: `${isMobile ? arrowLeft : 24}px` }}></div>
          )}
        </div>
      </div>
    </div>
  )
}
